import { useState, type CSSProperties, type ImgHTMLAttributes } from "react";
import { resolveStaticImage, staticImageCandidates } from "@/lib/static-image";

type ResponsiveImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string;
  fill?: boolean;
  priority?: boolean;
};

export function ResponsiveImage({ fill = false, priority = false, style, loading, decoding, fetchPriority,
  src, srcSet, sizes, width, onError, ...props }: ResponsiveImageProps) {
  const [failedSource, setFailedSource] = useState<string>();
  const fixedWidth = !fill && !sizes && Number(width) > 0 ? Number(width) : undefined;
  const widths = fixedWidth ? [fixedWidth, fixedWidth * 2] : [32, 64, 96, 128, 192, 256, 320, 480, 640, 960, 1280, 1600, 1920];
  const remote = resolveStaticImage(src, fixedWidth ?? 960);
  const fallback = failedSource === src;
  const candidates = fallback ? undefined : srcSet ?? staticImageCandidates(src, widths);
  const fillStyle: CSSProperties | undefined = fill
    ? { position: "absolute", inset: 0, width: "100%", height: "100%", ...style }
    : style;

  return (
    <img
      {...props}
      src={fallback ? src : remote}
      srcSet={candidates}
      sizes={sizes ?? (candidates ? fixedWidth ? `${fixedWidth}px` : "100vw" : undefined)}
      width={width}
      style={fillStyle}
      loading={priority ? "eager" : loading ?? "lazy"}
      decoding={decoding ?? "async"}
      fetchPriority={priority ? "high" : fetchPriority}
      onError={(event) => {
        if (remote !== src && !fallback) setFailedSource(src);
        onError?.(event);
      }}
    />
  );
}
